import React, { useEffect, useRef, useState } from "react";
import { Stethoscope, Plus, Pencil, Trash2, BadgeCheck, Mail, Phone, CheckCircle2, XCircle, Camera, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { maskCPF } from "../utils/masks";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const CATEGORIAS = ["Médico(a)", "Nutricionista", "Fisioterapeuta", "Enfermeiro(a)", "Outro"];
const CONSELHO_POR_CATEGORIA = { "Médico(a)": "CRM", "Nutricionista": "CRN", "Fisioterapeuta": "CREFITO", "Enfermeiro(a)": "COREN", "Outro": "Outro" };
const empty = { nome: "", categoria: "Médico(a)", conselho: "CRM", registro: "", registroUf: "SP", especialidade: "", cpf: "", dataNascimento: "", telefone: "", email: "", foto: "", ativo: true };

const Profissionais = ({ embutido = false }) => {
  const { getProfissionais, createProfissional, updateProfissional, deleteProfissional } = useApp();
  const { toast } = useToast();
  const [lista, setLista] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const fileRef = useRef(null);

  const carregar = async () => setLista(await getProfissionais());
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  const abrirNovo = () => { setEditing(null); setForm(empty); setOpen(true); };
  const abrirEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCategoria = (v) => setForm((f) => ({ ...f, categoria: v, conselho: CONSELHO_POR_CATEGORIA[v] || f.conselho }));

  const handleFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, foto: reader.result }));
    reader.readAsDataURL(file);
  };

  const salvar = async () => {
    if (!form.nome) { toast({ title: "Informe o nome do profissional" }); return; }
    try {
      if (editing) await updateProfissional(editing.id, form);
      else await createProfissional(form);
      toast({ title: editing ? "Profissional atualizado" : "Profissional cadastrado" });
      setOpen(false); carregar();
    } catch (e) {
      toast({ title: "Erro", description: e?.response?.data?.detail || "Não foi possível salvar." });
    }
  };
  const remover = async (p) => { await deleteProfissional(p.id); carregar(); };

  return (
    <div className="space-y-6" data-testid="profissionais-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          {embutido ? <p className="text-slate-500 text-sm">Prescritores usados no receituário (nome + registro no conselho). Quem tem CPF recebe acesso automático.</p> : (<>
            <h1 className="text-2xl font-bold text-slate-800">Profissionais / Médicos</h1>
            <p className="text-slate-400 text-sm mt-0.5">Cadastro de prescritores usados no receituário (nome + registro no conselho)</p>
          </>)}
        </div>
        <Button onClick={abrirNovo} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="novo-profissional-btn"><Plus className="w-4 h-4" /> Novo Profissional</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">Nome</th>
                <th className="px-5 py-3 font-medium">Categoria</th>
                <th className="px-5 py-3 font-medium">Registro</th>
                <th className="px-5 py-3 font-medium">Especialidade</th>
                <th className="px-5 py-3 font-medium">Contato</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {lista.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">Nenhum profissional cadastrado.</td></tr>}
              {lista.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3.5 font-medium text-slate-700 flex items-center gap-2">
                    {p.foto ? <img src={p.foto} alt={p.nome} className="w-8 h-8 rounded-lg object-cover" />
                      : <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Stethoscope className="w-4 h-4" /></div>}
                    {p.nome}
                  </td>
                  <td className="px-5 py-3.5 text-slate-500">{p.categoria || "—"}</td>
                  <td className="px-5 py-3.5"><span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md"><BadgeCheck className="w-3.5 h-3.5" /> {p.conselho} {p.registro}{p.registroUf ? `-${p.registroUf}` : ""}</span></td>
                  <td className="px-5 py-3.5 text-slate-500">{p.especialidade || "-"}</td>
                  <td className="px-5 py-3.5 text-slate-500">
                    {p.telefone && <span className="inline-flex items-center gap-1 mr-2"><Phone className="w-3.5 h-3.5" /> {p.telefone}</span>}
                    {p.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {p.email}</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    {p.ativo ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Ativo</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400"><XCircle className="w-3.5 h-3.5" /> Inativo</span>}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button onClick={() => abrirEdit(p)} className="text-slate-400 hover:text-blue-600 p-1.5"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remover(p)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{editing ? "Editar Profissional" : "Novo Profissional"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 flex items-center gap-3">
              <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                {form.foto ? <img src={form.foto} alt="foto" className="w-full h-full object-cover" data-testid="prof-foto-preview" />
                  : <Stethoscope className="w-6 h-6 text-slate-300" />}
                {form.foto && (
                  <button type="button" onClick={() => s("foto", "")} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                )}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} data-testid="prof-foto-input" />
                <Button type="button" variant="outline" className="gap-2" onClick={() => fileRef.current?.click()} data-testid="prof-foto-btn"><Camera className="w-4 h-4" /> Enviar foto</Button>
                <p className="text-xs text-slate-400 mt-1">Foto do profissional (opcional)</p>
              </div>
            </div>
            <div className="sm:col-span-2"><label className="text-sm font-medium text-slate-600 mb-1 block">Nome</label>
              <input className={inputCls} data-testid="prof-nome" value={form.nome} onChange={(e) => s("nome", e.target.value)} /></div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Categoria</label>
              <Select value={form.categoria} onValueChange={setCategoria}>
                <SelectTrigger data-testid="prof-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Conselho</label>
              <Select value={form.conselho} onValueChange={(v) => s("conselho", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["CRM", "COREN", "CRF", "CRN", "CREFITO", "Outro"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><label className="text-sm font-medium text-slate-600 mb-1 block">Nº do registro</label>
                <input className={inputCls} data-testid="prof-registro" value={form.registro} onChange={(e) => s("registro", e.target.value)} /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">UF</label>
                <input className={inputCls} maxLength={2} value={form.registroUf} onChange={(e) => s("registroUf", e.target.value.toUpperCase())} /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">CPF</label>
              <input className={inputCls} data-testid="prof-cpf" placeholder="000.000.000-00" value={form.cpf} onChange={(e) => s("cpf", maskCPF(e.target.value))} /></div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Data de nascimento</label>
              <input type="date" className={inputCls} data-testid="prof-nascimento" value={form.dataNascimento || ""} onChange={(e) => s("dataNascimento", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className="text-sm font-medium text-slate-600 mb-1 block">Especialidade</label>
              <input className={inputCls} value={form.especialidade} onChange={(e) => s("especialidade", e.target.value)} /></div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Telefone</label>
              <input className={inputCls} value={form.telefone} onChange={(e) => s("telefone", e.target.value)} /></div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">E-mail</label>
              <input className={inputCls} value={form.email} onChange={(e) => s("email", e.target.value)} /></div>
          </div>
          <div className="text-xs text-slate-500 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2" data-testid="prof-acesso-info">
            Ao salvar, é criado automaticamente um <b>acesso ao sistema</b> (Médico(a)/Enfermeiro(a)/Nutricionista/Fisioterapeuta):
            login = <b>CPF</b> (só números) e senha inicial = <b>6 primeiros dígitos do CPF</b>. Informe o CPF para gerar o acesso.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="prof-salvar" onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profissionais;
